/**
 * <product-tabs> — Web Component for the product info tabs (Specifications,
 * Care + Warrantee, Shipping + Returns, Installation).
 *
 * Server renders the tab bar as anchor links and all four panels stacked.
 * Without JS, all panels are visible and the tab bar scrolls between them.
 * With JS, this component upgrades the markup to ARIA tablist semantics:
 * only the active panel is visible, arrow-keys cycle tabs, the URL hash
 * drives the initial active tab.
 *
 * Markup contract:
 *   <product-tabs>
 *     <nav data-tabs-nav>
 *       <ul>
 *         <li><a data-tab data-target="tab-specifications" href="#tab-specifications">...</a></li>
 *         ...
 *       </ul>
 *     </nav>
 *     <div data-tabs-panels>
 *       <section data-panel data-name="tab-specifications" id="tab-specifications">...</section>
 *       ...
 *     </div>
 *   </product-tabs>
 *
 * Keyboard (per the WAI-ARIA tablist pattern):
 *   ArrowRight / ArrowLeft — move focus to next/previous tab and activate
 *   Home / End             — jump to first/last tab and activate
 */

class ProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));
    this.tablist = this.querySelector('[data-tabs-nav] ul');

    if (this.tabs.length === 0 || this.panels.length === 0) {
      return;
    }

    if (this.tablist) {
      this.tablist.setAttribute('role', 'tablist');
    }

    this.tabs.forEach((tab, index) => {
      const target = tab.dataset.target;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('id', `tabbtn-${target}`);
      tab.setAttribute('aria-controls', target);
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
      tab.parentElement.setAttribute('role', 'presentation');
      tab.dataset.tabIndex = String(index);

      tab.addEventListener('click', this.onTabClick);
      tab.addEventListener('keydown', this.onTabKeydown);
    });

    this.panels.forEach((panel) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', `tabbtn-${panel.dataset.name}`);
      panel.setAttribute('tabindex', '0');
      panel.classList.remove('mt-16');
    });

    const initial = this.resolveInitialTab();
    this.activate(initial, { focus: false, scroll: false });
  }

  resolveInitialTab() {
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      const match = this.tabs.find((tab) => tab.dataset.target === hash);
      if (match) return match;
    }
    return this.tabs[0];
  }

  onTabClick = (event) => {
    event.preventDefault();
    this.activate(event.currentTarget, { focus: true, scroll: false });
  };

  onTabKeydown = (event) => {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(this.tabs[nextIndex], { focus: true, scroll: false });
  };

  activate(targetTab, { focus, scroll }) {
    if (!targetTab) return;
    const targetName = targetTab.dataset.target;

    this.tabs.forEach((tab) => {
      const selected = tab === targetTab;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.setAttribute('tabindex', selected ? '0' : '-1');
      if (selected) {
        tab.classList.add('text-ink-strong', 'border-accent');
        tab.classList.remove('text-ink', 'border-transparent');
      } else {
        tab.classList.remove('text-ink-strong', 'border-accent');
        tab.classList.add('text-ink', 'border-transparent');
      }
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.dataset.name !== targetName;
    });

    if (focus) {
      targetTab.focus();
    }
    if (scroll) {
      targetTab.scrollIntoView({ block: 'nearest' });
    }

    if (focus && window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.hash = targetName;
      window.history.replaceState(null, '', url.toString());
    }
  }
}

if (!customElements.get('product-tabs')) {
  customElements.define('product-tabs', ProductTabs);
}
